import { useState } from 'react'
import { Download, FileText, File, BookOpen, ChevronDown } from 'lucide-react'
import { api } from '@/lib/api'
import { notify } from '@/lib/notify'

interface Props {
  sessionId: string
  disabled?: boolean
}

const FORMATS = [
  { id: 'docx' as const, label: 'Word (.docx)', icon: FileText, description: 'Professional Word document' },
  { id: 'pdf' as const, label: 'PDF (.pdf)', icon: File, description: 'Portable Document Format' },
  { id: 'epub' as const, label: 'EPUB (.epub)', icon: BookOpen, description: 'E-book format' },
  { id: 'md' as const, label: 'Markdown (.md)', icon: FileText, description: 'Raw markdown text' },
]

export function StudioExportMenu({ sessionId, disabled }: Props) {
  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)

  const handleExport = async (format: 'md' | 'docx' | 'pdf' | 'epub') => {
    setExporting(format)
    setOpen(false)
    try {
      await api.studio.export(sessionId, format)
      notify.success(`Exported as ${format.toUpperCase()}`)
    } catch (err) {
      notify.error('Export failed', { description: err instanceof Error ? err.message : String(err) })
    } finally {
      setExporting(null)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={disabled || !!exporting}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <Download className="w-3.5 h-3.5" />
        {exporting ? `Exporting ${exporting.toUpperCase()}...` : 'Download'}
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-white dark:bg-surface-800 rounded-lg border border-gray-200 dark:border-white/10 shadow-lg py-1">
            {FORMATS.map((fmt) => {
              const Icon = fmt.icon
              return (
                <button
                  key={fmt.id}
                  onClick={() => handleExport(fmt.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <Icon className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{fmt.label}</p>
                    <p className="text-xs text-gray-400">{fmt.description}</p>
                  </div>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
